import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Flame } from "lucide-react";

import { RoomSettingsPanel } from "@/components/camp/RoomSettingsPanel";
import { Button } from "@/components/ui/button";
import { CreatePreview } from "./CreatePreview";
import { campIdentityFor } from "@/components/room/campIdentity";
import { APP_MODE, MODES, PANEL_MODE } from "@/components/room/roomModePanel";
import { getSessionPeerId } from "@/engine/state/roomIdentity";
import { setSetting } from "@/engine/state/settings";
import { useAvatarChoice } from "@/hooks/useAvatarChoice";
import { useDisplayName } from "@/hooks/useDisplayName";
import { useHydrated } from "@/hooks/useHydrated";
import { useSettings } from "@/hooks/useSettings";

export interface LightAFireProps {
  /** A press is under way, or the account is not known yet. */
  busy: boolean;
  /** Which of the two the wait is, for its words. */
  creating: boolean;
  onCreate: (name: string) => void;
}

/**
 * Opening a room, as the brandbook's *Light a fire*: the name, the four kinds
 * of room, and the chosen kind as a live stage with the visitor in it — the
 * stage is the argument a caption cannot make. The way in is the page's own,
 * under the form, so it keeps the handle the application's tests hold.
 */
export function LightAFire({ busy, creating, onCreate }: LightAFireProps) {
  const { t } = useTranslation();
  const { roomMode } = useSettings();
  const [name, setName] = useState("");
  const hydrated = useHydrated();
  const you = campIdentityFor(
    hydrated ? getSessionPeerId() : "",
    useAvatarChoice(),
  );
  const yourName = useDisplayName() || t("avatar.namePlaceholder");

  return (
    <section
      data-testid="light-a-fire"
      className="space-y-5 rounded-3xl border border-border bg-card p-5 sm:p-7"
    >
      <h2 className="text-xl font-semibold tracking-tight">
        {t("entry.lightAFire")}
      </h2>
      <RoomSettingsPanel
        name={name}
        nameLabel={t("entry.roomNameLabel")}
        onNameChange={setName}
        mode={PANEL_MODE[roomMode]}
        modeLabel={t("roomMode.title")}
        options={MODES.map((mode) => ({
          id: PANEL_MODE[mode],
          name: t(`roomMode.${mode}`),
          description: t(`roomMode.${mode}Caption`),
        }))}
        onModeChange={(mode) => setSetting("roomMode", APP_MODE[mode])}
        preview={
          <CreatePreview mode={roomMode} you={{ name: yourName, ...you }} />
        }
        testIds={{
          name: "create-room-name",
          plain: "room-mode-card-plain",
          teams: "room-mode-card-teams",
          bubbles: "room-mode-card-bubbles",
          fire: "room-mode-card-campfire",
        }}
        // Where the stage cannot sit beside the four it is not drawn, so the
        // way in stays right under them rather than a screen further down.
        className="max-lg:[&>*:nth-child(2)]:hidden"
      />
      {/* Under the form's own column, where the name and the four are. */}
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
        <div className="space-y-3">
          <Button
            data-testid="entry-create"
            size="xl"
            onClick={() => onCreate(name)}
            disabled={busy}
            className="w-full min-h-14 rounded-2xl text-sm font-semibold sm:text-base"
          >
            {busy ? (
              <>
                <span
                  aria-hidden="true"
                  className="mr-2 size-5 animate-spin rounded-full border-2 border-primary-foreground/30 border-t-primary-foreground"
                />
                {creating ? t("entry.creating") : t("entry.checkingAccount")}
              </>
            ) : (
              <>
                <Flame aria-hidden="true" />
                {t("entry.createRoom")}
              </>
            )}
          </Button>
          <p
            data-testid="entry-create-hint"
            className="text-center text-xs text-muted-foreground"
          >
            {t("entry.createHint")}
          </p>
        </div>
      </div>
    </section>
  );
}
